import React, { useState, useEffect } from "react";
import { Button } from "../../../../design/button";
import {
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import { useImport } from "../../../../contexts/ImportContext";
import { SideType } from "../../../../models/SideType";
import SelectFileStep from "../SelectFileStep";
import FilingDetailsStep from "./FilingDetailsStep";

interface Props {
  handleClose: () => void;
}

interface FormState {
  file: File | null;
  name: string;
  filingDate: string;
  side: SideType;
}

const ImportFiling: React.FC<Props> = ({ handleClose }) => {
  const { createFilingImport } = useImport();

  const [formState, setFormState] = useState<FormState>({
    file: null,
    name: "",
    filingDate: "",
    side: "",
  });

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (formState.file && !formState.name) {
      setFormState((prev) => ({ ...prev, name: formState.file!.name }));
    }
  }, [formState.file]);

  const handleSubmit = async () => {
    if (!formState.file || !formState.name.trim()) {
      setError("Name and file are required.");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      await createFilingImport(formState.file);
      handleClose();
    } catch {
      setError("Failed to create filing. Please try again.");
    }

    setIsUploading(false);
  };

  const handleCancel = () => {
    if (formState.file) {
      setFormState((prev) => ({ ...prev, file: null }));
    } else {
      handleClose();
    }
  };

  return (
    <>
      <DialogTitle>Import Filing</DialogTitle>
      <DialogBody>
        {!formState.file ? (
          <SelectFileStep
            onFileSelected={(file) =>
              setFormState((prev) => ({ ...prev, file }))
            }
          />
        ) : (
          <FilingDetailsStep
            formState={formState}
            setFormState={setFormState}
            error={error}
          />
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          disabled={!formState.file || isUploading}
          onClick={handleSubmit}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </>
  );
};

export default ImportFiling;
